import { z } from "zod";
import { bodyDrawingViewSchema } from "./body-drawings.ts";

/**
 * Treatment Plan Map — non-generative aesthetic planning. The clinician pins
 * planned procedures onto anatomical zones of a generic mannequin (reusing the
 * body-drawing 0..1000 viewBox + view set). NEVER a rendering of the patient.
 */

/** Planned procedure class for a pin — drives the pin colour/icon on the client. */
export const treatmentPlanProcedureSchema = z.enum([
	"botox",
	"filler",
	"laser",
	"hair_removal",
	"thread",
	"prp",
	"peel",
	"mesotherapy",
	"surgical",
	"other",
]);
export type TreatmentPlanProcedure = z.infer<
	typeof treatmentPlanProcedureSchema
>;

export const treatmentPlanPinSchema = z.object({
	/** Reuses the body-drawing view set (face_front/left/right, body, scalp, hands, custom). */
	view: bodyDrawingViewSchema,
	x: z.number().min(0).max(1000),
	y: z.number().min(0).max(1000),
	procedure: treatmentPlanProcedureSchema,
	/** e.g. "20U", "1ml", "3 sessions". */
	label: z.string().max(80).optional(),
	color: z.string().max(32).optional(),
});
export type TreatmentPlanPin = z.infer<typeof treatmentPlanPinSchema>;

export const treatmentPlanMapApiSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	patient_id: z.string().uuid(),
	visit_id: z.string().uuid().nullable(),
	title: z.string().nullable(),
	pins: z.array(treatmentPlanPinSchema),
	caveat: z.string().nullable(),
	created_by_member_id: z.string().uuid().nullable(),
	created_by_name: z.string().nullable(),
	created_at: z.string().datetime(),
	updated_at: z.string().datetime(),
});
export type TreatmentPlanMapApi = z.infer<typeof treatmentPlanMapApiSchema>;

export const createTreatmentPlanMapInputSchema = z.object({
	patient_id: z.string().uuid(),
	visit_id: z.string().uuid().optional(),
	title: z.string().max(160).optional(),
	pins: z.array(treatmentPlanPinSchema).max(200).default([]),
});
export type CreateTreatmentPlanMapInput = z.infer<
	typeof createTreatmentPlanMapInputSchema
>;

export const updateTreatmentPlanMapInputSchema = z.object({
	title: z.string().max(160).nullable().optional(),
	pins: z.array(treatmentPlanPinSchema).max(200).optional(),
});
export type UpdateTreatmentPlanMapInput = z.infer<
	typeof updateTreatmentPlanMapInputSchema
>;
